<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiIcon, UiChip, UiStandardButton, UiTooltip } from "@shopexperts/shared";
import type { AiSummaryKind, ExpertAiCurrentSummary } from "@shopexperts/legacy-api";
import { AI_SUMMARY_KIND_LABELS, formatAiDateTime } from "@/app/composables/expert-manager/aiSummaries";

const props = withDefaults(
  defineProps<{
    kind: AiSummaryKind;
    summary: ExpertAiCurrentSummary | null;
    isBusy?: boolean;
    canRegenerate?: boolean;
  }>(),
  { isBusy: false, canRegenerate: true },
);

const emit = defineEmits<{
  (e: "regenerate"): void;
  (e: "history"): void;
  (e: "view-run", runId: number): void;
}>();

const title = computed(() => AI_SUMMARY_KIND_LABELS[props.kind]);
const structured = computed(() => props.summary?.structured ?? null);
const positives = computed(() =>
  props.kind === "profile" ? structured.value?.recommended_for ?? [] : structured.value?.most_common_praise ?? [],
);
const negatives = computed(() =>
  props.kind === "profile" ? structured.value?.not_ideal_for ?? [] : structured.value?.most_common_concerns ?? [],
);
const positivesLabel = computed(() => (props.kind === "profile" ? "Recommended for" : "Most common praise"));
const negativesLabel = computed(() => (props.kind === "profile" ? "May not be ideal for" : "Most common concerns"));
const reviewCount = computed(() => (props.kind === "reviews" ? structured.value?.review_count ?? null : null));
const emptyText = computed(() =>
  props.kind === "profile"
    ? "Nothing published yet. Generate the pipeline to write the first expert summary."
    : "Nothing published yet. The reviews summary is skipped for experts without published reviews.",
);
</script>

<template>
  <article class="flex h-full flex-col gap-4 rounded-xl border border-se-gray-200 bg-se-white p-5">
    <header class="flex flex-wrap items-start justify-between gap-3">
      <div class="flex min-w-0 flex-col gap-1">
        <div class="flex items-center gap-2">
          <UiIcon name="star-06" class="h-4 w-4 shrink-0 text-se-green" />
          <UiText as="h3" variant="h6" class="font-semibold text-se-dark" :text="title" />
          <UiChip v-if="summary" text="Live on profile" variant="success" size="xs" rounded="full" />
        </div>
        <UiText
          v-if="summary"
          as="p"
          variant="caption-normal"
          class="text-se-gray-500"
          :text="`Revision ${summary.revision} · run #${summary.run_id} · published ${formatAiDateTime(summary.published_at)}${reviewCount ? ` · ${reviewCount} reviews` : ''}`"
        />
      </div>
      <div class="flex shrink-0 items-center gap-1.5">
        <UiTooltip content="Every earlier revision, with the option to make one live again">
          <UiStandardButton variant="ghost" size="sm" icon="clock" label="History" @click="emit('history')" />
        </UiTooltip>
        <UiTooltip v-if="summary" content="Open the run that produced this text">
          <UiStandardButton variant="ghost" size="sm" icon="file-placeholder" label="Run" @click="emit('view-run', summary.run_id)" />
        </UiTooltip>
        <UiTooltip content="Rewrite this summary from the latest normalized evidence">
          <UiStandardButton
            variant="outline"
            size="sm"
            icon="refresh-cw"
            label="Regenerate"
            :disabled="isBusy || !canRegenerate"
            @click="emit('regenerate')"
          />
        </UiTooltip>
      </div>
    </header>

    <template v-if="summary">
      <UiText as="p" variant="p-small" class="text-se-dark" :text="summary.content" />
      <div v-if="positives.length || negatives.length" class="grid gap-4 sm:grid-cols-2">
        <div v-if="positives.length" class="flex flex-col gap-2">
          <UiText as="span" variant="caption" class="text-se-gray-500" :text="positivesLabel" />
          <ul class="flex flex-col gap-1.5">
            <li v-for="(item, index) in positives" :key="`p-${index}`" class="flex items-center gap-2">
              <UiIcon name="check-circle-outline" class="h-4 w-4 shrink-0 text-se-green" />
              <UiText as="span" variant="p-small" :text="item" />
            </li>
          </ul>
        </div>
        <div v-if="negatives.length" class="flex flex-col gap-2">
          <UiText as="span" variant="caption" class="text-se-gray-500" :text="negativesLabel" />
          <ul class="flex flex-col gap-1.5">
            <li v-for="(item, index) in negatives" :key="`n-${index}`" class="flex items-center gap-2">
              <UiIcon name="x-circle" class="h-4 w-4 shrink-0 text-se-danger" />
              <UiText as="span" variant="p-small" :text="item" />
            </li>
          </ul>
        </div>
      </div>
    </template>

    <div
      v-else
      class="flex flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-se-gray-300 px-4 py-8 text-center"
    >
      <UiIcon name="star-06" class="h-6 w-6 text-se-gray-300" />
      <UiText as="p" variant="p-small" class="max-w-xs text-se-gray-500" :text="emptyText" />
    </div>
  </article>
</template>
